import { resolveThreadSettleAvailability } from "./threadSettleAvailability";
import type { SidebarRuntimeActions } from "./useSidebarThreadHandlers.helpers";
import {
  buildThreadTitleRegenerationMenuItem,
  type ThreadTitleRegenerationMenuItem,
} from "./threadTitleRegenerationMenu";

/**
 * The thread action menu, built once.
 *
 * Forge assembled this inline inside `useSidebarThreadHandlers`, in two places
 * that had already drifted: the single-thread menu carried eleven items and the
 * multi-select menu carried two. Every item added since had to be added twice,
 * and in practice was not.
 *
 * Ported in shape from t3code's `threadActionMenu.logic.ts`, whose own comment
 * states the reason: one source, rendered by the sidebar row's right-click and
 * the chat header alike, so labels and ordering cannot disagree between the
 * surfaces showing the same thread.
 *
 * **Not** ported in content. Forge's menu is richer where theirs is thin —
 * `runtime-stop`, `runtime-start-fresh`, `runtime-continue` and `runtime-retry`
 * have no t3code counterpart, and adopting their item list wholesale would
 * delete four working actions. This module is the union, and its test
 * enumerates all eleven single-thread ids so a later edit cannot quietly drop
 * one.
 *
 * @module threadActionMenu.logic
 */

/** Every id either menu can produce. */
export type ThreadActionMenuId =
  | "new-thread-on-branch"
  | "rename"
  | "regenerate-title"
  | "toggle-pin"
  | "runtime-stop"
  | "runtime-start-fresh"
  | "runtime-continue"
  | "runtime-retry"
  | "mark-unread"
  | "copy"
  | "copy-path"
  | "copy-branch"
  | "copy-thread-id"
  | "settle"
  | "unsettle"
  | "archive"
  | "unarchive"
  | "delete";

export interface ThreadActionMenuItem {
  readonly id: ThreadActionMenuId;
  readonly label: string;
  readonly disabled?: true;
  readonly destructive?: true;
  /**
   * Nested actions. A parent carrying these is structural and never resolves —
   * only a child's id comes back — so the click handlers stay unchanged.
   */
  readonly children?: ReadonlyArray<ThreadActionMenuItem>;
}

/**
 * What the single-thread menu needs to know.
 *
 * A flat record rather than the thread itself, so the builder stays pure and
 * the caller keeps ownership of how each fact is derived — `isPinned` comes
 * from a UI store, `isRegenerating` from thread metadata, and `runtimeActions`
 * from session state.
 */
export interface ThreadActionMenuState {
  readonly isPinned: boolean;
  readonly isRegenerating: boolean;
  readonly runtimeActions: SidebarRuntimeActions;
  /**
   * The thread's branch, when it has one.
   *
   * Gates two items rather than disabling them: "New thread on …" has no name
   * to render without it, and "Copy Branch" would copy nothing. An item that
   * cannot state its own subject is worse than an absent one.
   */
  readonly branch?: string | null;
  /**
   * Whether this surface can act on the branch by starting a thread on it.
   *
   * Separate from `branch` on purpose. The sidebar used to fold the two
   * together — it passed `branch` only when it also owned a new-thread
   * handler — which meant a caller without that handler silently lost **Copy
   * Branch** as well, an item that needs nothing from the caller but the
   * branch name. Copying a branch and starting a thread on it are different
   * capabilities and are gated separately.
   *
   * Defaults to true: a surface that knows the branch can usually navigate.
   * The one caller that cannot passes `false` explicitly.
   */
  readonly canStartNewThreadOnBranch?: boolean;
  /**
   * Whether this thread is already on the archived shelf.
   *
   * Swaps one slot rather than adding an item: a thread is in exactly one of
   * the two states, so offering both invites a click that cannot apply. The
   * shelf's right-click used to offer "Archive thread" on an already-archived
   * thread and no way back at all — the one reversal the shelf exists for.
   */
  readonly isArchived?: boolean;
  /**
   * Whether this surface owns an unarchive handler.
   *
   * Same shape as `canStartNewThreadOnBranch`, and gated for the same reason:
   * two callers mount the shared handler and only the archived shelf owns the
   * mutation. Where it is absent the slot is EMPTY rather than falling back to
   * "Archive thread" — offering to archive something already archived is the
   * bug this whole field exists to fix, and the project rows that lack the
   * handler never list an archived thread anyway.
   */
  readonly canUnarchive?: boolean;
  /** Exact CMMD archive workflow state, when an owner command exists. */
  readonly archiveState?: "requested" | "applying" | "archived" | "failed" | null;
  /**
   * Whether this thread is already on the Settled shelf.
   *
   * Same one-slot-not-two shape as `isArchived`/`canUnarchive`: a thread is
   * either settled or it isn't, so the menu swaps Settle for Un-settle rather
   * than ever showing a click that cannot apply.
   */
  readonly isSettled?: boolean;
  /**
   * Whether this surface owns an unsettle handler.
   *
   * Mirrors `canUnarchive`. Where it is absent the slot is EMPTY rather than
   * falling back to "Settle thread" — offering to settle something already
   * settled is the same bug `canUnarchive` exists to prevent.
   */
  readonly canUnsettle?: boolean;
}

/**
 * The menu for one thread, in the order it renders.
 *
 * Order is part of the contract: destructive last, runtime actions grouped
 * after the identity actions they interrupt. The test pins the full sequence,
 * not just membership, because a menu that silently reorders moves Delete under
 * the user's cursor.
 */
export function buildThreadActionMenuItems(
  state: ThreadActionMenuState,
): ReadonlyArray<ThreadActionMenuItem> {
  const { isPinned, isRegenerating, runtimeActions } = state;

  const branch = state.branch?.trim() ?? "";

  return [
    // First, like the reference: starting sibling work on the same branch is a
    // navigation action, not a mutation of this thread.
    ...(branch.length > 0 && state.canStartNewThreadOnBranch !== false
      ? ([{ id: "new-thread-on-branch", label: `New thread on ${branch}` }] as const)
      : []),
    { id: "rename", label: "Rename thread" },
    ...(buildThreadTitleRegenerationMenuItem({
      isRegenerating,
    }) as ReadonlyArray<ThreadTitleRegenerationMenuItem & ThreadActionMenuItem>),
    { id: "toggle-pin", label: isPinned ? "Unpin thread" : "Pin thread" },
    ...buildSettleMenuItems(state),
    ...(runtimeActions.canStop ? ([{ id: "runtime-stop", label: "Stop" }] as const) : []),
    ...(runtimeActions.canStartFreshSession
      ? ([{ id: "runtime-start-fresh", label: "Start fresh provider session…" }] as const)
      : []),
    ...(runtimeActions.canContinue
      ? ([{ id: "runtime-continue", label: "Continue" }] as const)
      : []),
    ...(runtimeActions.canRetry
      ? ([{ id: "runtime-retry", label: "Retry last prompt" }] as const)
      : []),
    { id: "mark-unread", label: "Mark unread" },
    // Grouped, matching t3code: three copies under one parent so they read as
    // one affordance rather than three competing lines. The parent is
    // structural — it has no id that can resolve — so every handler still
    // switches on `copy-path` / `copy-branch` / `copy-thread-id` exactly as
    // before. Electron renders this as a submenu; the web fallback renders it
    // as a labelled group, which is the same actions in one click.
    {
      id: "copy",
      label: "Copy",
      children: [
        { id: "copy-path", label: "Path" },
        ...(branch.length > 0 ? ([{ id: "copy-branch", label: "Branch" }] as const) : []),
        { id: "copy-thread-id", label: "Thread ID" },
      ],
    },
    ...buildShelfMenuItems(state),
    { id: "delete", label: "Delete", destructive: true },
  ];
}

/**
 * The one slot that moves a thread on or off the archived shelf.
 *
 * Never both items: a thread is in exactly one of the two states, so showing
 * both would put a click that cannot apply next to the one that can.
 */
function buildShelfMenuItems(
  state: Pick<ThreadActionMenuState, "archiveState" | "isArchived" | "canUnarchive">,
): ReadonlyArray<ThreadActionMenuItem> {
  const archiveStateItems = buildArchiveStateMenuItems(
    state.archiveState ?? null,
    state.canUnarchive,
  );
  if (archiveStateItems !== null) return archiveStateItems;
  if (!state.isArchived) return [{ id: "archive", label: "Archive thread" }];
  return state.canUnarchive === false ? [] : [{ id: "unarchive", label: "Restore thread" }];
}

function buildArchiveStateMenuItems(
  archiveState: ThreadActionMenuState["archiveState"],
  canUnarchive: boolean | undefined,
): ReadonlyArray<ThreadActionMenuItem> | null {
  if (archiveState === null) return null;
  if (archiveState === "applying") return [];
  if (archiveState === "failed") {
    const cancelItem: ReadonlyArray<ThreadActionMenuItem> =
      canUnarchive === false ? [] : [{ id: "unarchive", label: "Cancel archive" }];
    return [{ id: "archive", label: "Retry archive" }, ...cancelItem];
  }
  const label = archiveState === "requested" ? "Cancel archive" : "Restore thread";
  return canUnarchive === false ? [] : [{ id: "unarchive", label } as const];
}

/**
 * The one slot that moves a thread on or off the Settled shelf.
 *
 * Never both items, for the same reason as `buildShelfMenuItems`: a thread is
 * in exactly one of the two states.
 *
 * The decision itself lives in `resolveThreadSettleAvailability`, shared with
 * the Activity card's hover slot. The two used to decide separately and drifted
 * — the card hid its control on a running thread, this menu kept offering the
 * action, and the command behind it then threw — so the rule is one function
 * and this is only its rendering.
 */
function buildSettleMenuItems(
  state: Pick<ThreadActionMenuState, "archiveState" | "isSettled" | "canUnsettle">,
): ReadonlyArray<ThreadActionMenuItem> {
  switch (resolveThreadSettleAvailability(state)) {
    case "settle":
      return [{ id: "settle", label: "Settle thread" }];
    case "unsettle":
      return [{ id: "unsettle", label: "Un-settle thread" }];
    case "none":
      return [];
  }
}

/**
 * The menu for a multi-selection.
 *
 * Takes a count rather than a thread, because the two menus are not the same
 * list with different labels: a bulk Rename has no meaning, and a bulk Copy
 * Thread ID would have to invent a separator. Sharing a module is worth doing;
 * pretending these are one function is not.
 */
export function buildMultiSelectThreadActionMenuItems(input: {
  readonly count: number;
  /**
   * How many of the selected threads could have a title regenerated at all.
   *
   * Zero means the item is absent rather than disabled — there is nothing to
   * offer, and a permanently greyed row is noise.
   */
  readonly regenerableCount?: number;
  /** How many of those are not already regenerating. */
  readonly regenerationIdleCount?: number;
}): ReadonlyArray<ThreadActionMenuItem> {
  const { count, regenerableCount = 0, regenerationIdleCount = 0 } = input;
  if (count === 0) return [];

  return [
    ...buildBulkTitleRegenerationMenuItem({
      supportedCount: regenerableCount,
      actionableCount: regenerationIdleCount,
    }),
    { id: "mark-unread", label: `Mark unread (${count})` },
    { id: "delete", label: `Delete (${count})`, destructive: true },
  ];
}

/**
 * The bulk "Regenerate titles" entry, in t3code's exact three states.
 *
 * Absent when nothing in the selection supports it, disabled while every
 * candidate is already running, actionable otherwise. Collapsing that to two
 * states is the easy mistake: absent-vs-disabled is the difference between
 * "this cannot apply here" and "this is busy", and a user who sees neither
 * assumes the feature does not exist.
 *
 * Counts the ACTIONABLE threads in the label, not the whole selection —
 * selecting ten threads of which two can be retitled offers "Regenerate titles
 * (2)", because that is what the click will do.
 */
export function buildBulkTitleRegenerationMenuItem(input: {
  readonly supportedCount: number;
  readonly actionableCount: number;
}): ReadonlyArray<ThreadActionMenuItem> {
  if (input.supportedCount === 0) return [];
  if (input.actionableCount === 0) {
    return [
      {
        id: "regenerate-title",
        label: `Regenerating… (${input.supportedCount})`,
        disabled: true,
      },
    ];
  }
  return [{ id: "regenerate-title", label: `Regenerate titles (${input.actionableCount})` }];
}
